import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  coachSessionsEndpoints,
  IBlockBase,
  ICoachSession,
  IUpdateCoachSessionBody,
  BlockType,
} from '../../endpoints/coachSessions';

export type PanelTab = 'roster' | 'analysis';

const AUTOSAVE_DELAY_MS = 10_000;

function newBlock(type: BlockType, order: number): IBlockBase {
  return {
    order,
    blockType: type,
    durationMinutes: 15,
    transitionAfterMinutes: 2,
  };
}

export function useCoachSessionDetail(sessionId: string) {
  const queryClient = useQueryClient();

  // ─── Fetch ────────────────────────────────────────────────────────────────
  const { data: session, isLoading, error } = useQuery({
    queryKey: ['coachSession', sessionId],
    queryFn: () =>
      coachSessionsEndpoints.get(sessionId).then((r) => r.data),
    enabled: !!sessionId,
  });

  // ─── Local draft state ────────────────────────────────────────────────────
  const [localTitle, setLocalTitle] = useState('');
  const [localBlocks, setLocalBlocks] = useState<IBlockBase[]>([]);
  const [localObjective, setLocalObjective] = useState<string>('');
  const [localDate, setLocalDate] = useState('');
  const [localStartTime, setLocalStartTime] = useState('');

  useEffect(() => {
    if (session) {
      setLocalTitle(session.title);
      setLocalBlocks(
        [...(session.blocks ?? [])].sort((a, b) => a.order - b.order),
      );
      setLocalObjective(session.objective ?? '');
      setLocalDate(session.scheduledDate?.slice(0, 10) ?? '');
      setLocalStartTime(session.startTime ?? '');
    }
  }, [session]);

  // ─── Panel tab ────────────────────────────────────────────────────────────
  const [panelTab, setPanelTab] = useState<PanelTab>('roster');

  // ─── Saving state ─────────────────────────────────────────────────────────
  const [isSaving, setIsSaving] = useState(false);
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['coachSession', sessionId] });
  }, [queryClient, sessionId]);

  // ─── Update mutation ─────────────────────────────────────────────────────
  const updateMut = useMutation({
    mutationFn: (body: IUpdateCoachSessionBody) =>
      coachSessionsEndpoints.update(sessionId, body).then((r) => r.data),
    onSuccess: invalidate,
  });

  // ─── Publish mutation ─────────────────────────────────────────────────────
  const publishMut = useMutation({
    mutationFn: () =>
      coachSessionsEndpoints
        .update(sessionId, { status: 'published' })
        .then((r) => r.data),
    onSuccess: invalidate,
  });

  // ─── Recalculate mutation ────────────────────────────────────────────────
  const recalcMut = useMutation({
    mutationFn: () =>
      coachSessionsEndpoints.recalculateMetrics(sessionId).then((r) => r.data),
    onSuccess: invalidate,
  });

  // ─── Save helper ─────────────────────────────────────────────────────────
  const buildBody = useCallback(
    (overrideBlocks?: IBlockBase[]): IUpdateCoachSessionBody => ({
      title: localTitle,
      scheduledDate: localDate,
      startTime: localStartTime || undefined,
      objective: (localObjective as any) || undefined,
      blocks: (overrideBlocks ?? localBlocks).map((b, idx) => ({
        ...b,
        order: idx,
      })),
    }),
    [localTitle, localDate, localStartTime, localObjective, localBlocks],
  );

  const saveDraft = useCallback(async () => {
    setIsSaving(true);
    try {
      await updateMut.mutateAsync(buildBody());
    } finally {
      setIsSaving(false);
    }
  }, [updateMut, buildBody]);

  // ─── Autosave (draft only) ────────────────────────────────────────────────
  useEffect(() => {
    if (session?.status !== 'draft') return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      saveDraft();
    }, AUTOSAVE_DELAY_MS);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [localTitle, localBlocks, localDate, localStartTime, localObjective]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Block operations ─────────────────────────────────────────────────────
  const addBlock = useCallback(
    (type: BlockType, atIndex?: number) => {
      setLocalBlocks((prev) => {
        const idx = atIndex !== undefined ? atIndex : prev.length;
        const block = newBlock(type, idx);
        const updated = [...prev.slice(0, idx), block, ...prev.slice(idx)];
        return updated.map((b, i) => ({ ...b, order: i }));
      });
    },
    [],
  );

  const updateBlock = useCallback(
    (index: number, patch: Partial<IBlockBase>) => {
      setLocalBlocks((prev) =>
        prev.map((b, i) => (i === index ? { ...b, ...patch } : b)),
      );
    },
    [],
  );

  const removeBlock = useCallback((index: number) => {
    setLocalBlocks((prev) =>
      prev.filter((_, i) => i !== index).map((b, i) => ({ ...b, order: i })),
    );
  }, []);

  const duplicateBlock = useCallback((index: number) => {
    setLocalBlocks((prev) => {
      const copy = { ...prev[index] };
      const updated = [...prev.slice(0, index + 1), copy, ...prev.slice(index + 1)];
      return updated.map((b, i) => ({ ...b, order: i }));
    });
  }, []);

  const moveBlock = useCallback((index: number, direction: 'up' | 'down') => {
    setLocalBlocks((prev) => {
      const newList = [...prev];
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= newList.length) return prev;
      [newList[index], newList[target]] = [newList[target], newList[index]];
      return newList.map((b, i) => ({ ...b, order: i }));
    });
  }, []);

  return {
    session,
    isLoading,
    error,
    // local state
    localTitle,
    setLocalTitle,
    localBlocks,
    setLocalBlocks,
    localObjective,
    setLocalObjective,
    localDate,
    setLocalDate,
    localStartTime,
    setLocalStartTime,
    // panel
    panelTab,
    setPanelTab,
    // mutations
    saveDraft,
    publishMut,
    recalcMut,
    isSaving,
    // block ops
    addBlock,
    updateBlock,
    removeBlock,
    duplicateBlock,
    moveBlock,
  };
}
